/**
 * What the Lists tab's badge counts, as a list of the things themselves
 * (a2-20). Pure and dependency-free (type-only imports, plus `calendar.ts`,
 * which has none of its own) so `test/attention.test.ts` runs it under
 * `node --test`.
 *
 * The badge was `useAttentionCount`, a hook that reduced the same two
 * filters straight to a number — so the number on the tab and anything the
 * Lists page said about it were two computations that only agreed by being
 * written the same way twice. Now the badge is `attentionItems(…).length`
 * and the Lists page's "1 needs you: …" line is `attentionItems(…)` itself:
 * one function, so the count and the things it counts cannot drift apart.
 *
 * The rule, from N2/B3: open items due today (the current region's day),
 * plus saved events whose next showing is in the next 48 hours. Tasks plan
 * T3 adds open items *overdue* (due before today, oldest first, ahead of
 * today's) — so the badge, the Lists line and the Today card all count them,
 * on purpose: a task you missed needs you more than one due tonight.
 */
import type { FeedRow, ListItem } from './types'
import { addDays, fmt, iso } from './calendar.ts'
import { clockIn, dayIn, zoneOfCity } from './zone.ts'

const MS_HOUR = 3_600_000
export const ATTENTION_WINDOW_MS = 48 * MS_HOUR

export type AttentionItem =
  | { kind: 'task'; id: string; listId: string; title: string; why: string }
  | { kind: 'saved'; id: string; entryId: string; title: string; why: string; at: number }

type StateLike = { entry_id: string; state: string }
/** `city` names the zone the row's showing is read in (C2.1); absent is T2's. */
type FeedLike = Pick<FeedRow, 'id' | 'title' | 'next_showing'> & { city?: string | null }
type ItemLike = Pick<ListItem, 'id' | 'list_id' | 'body' | 'done_at' | 'due_on'>

/** "today 19:30", "tomorrow 20:00", "Sat 20:00" — the day of `at` in `tz`
 *  (the row's own zone, C2.1) relative to today (`iso`, the current
 *  region's). A 48-hour window spans at most three calendar days, so anything
 *  past tomorrow is named by its weekday. */
export function whenLabel(at: Date, now: Date, tz: string): string {
  const day = dayIn(at, tz)
  const today = iso(now)
  const tomorrow = addDays(today, 1)
  const dayWord = day === today ? 'today' : day === tomorrow ? 'tomorrow' : fmt(day, { weekday: 'short' })
  return `${dayWord} ${clockIn(at, tz)}`
}

export function attentionItems(
  feed: readonly FeedLike[],
  state: readonly StateLike[],
  items: readonly ItemLike[],
  now: Date,
): AttentionItem[] {
  const today = iso(now)
  const t0 = now.getTime()
  const t1 = t0 + ATTENTION_WINDOW_MS

  const tasks: AttentionItem[] = items
    .filter((i): i is ItemLike & { due_on: string } => !i.done_at && !!i.due_on && i.due_on <= today)
    .sort((a, b) => (a.due_on < b.due_on ? -1 : a.due_on > b.due_on ? 1 : 0))
    .map(i => ({ kind: 'task', id: i.id, listId: i.list_id, title: i.body, why: i.due_on === today ? 'due today' : 'overdue' }))

  const savedIds = new Set(state.filter(r => r.state === 'saved').map(r => r.entry_id))
  const saved: AttentionItem[] = []
  for (const e of feed) {
    if (!savedIds.has(e.id) || !e.next_showing) continue
    const at = Date.parse(e.next_showing)
    if (Number.isNaN(at) || at < t0 || at > t1) continue
    saved.push({
      kind: 'saved', id: e.id, entryId: e.id, title: e.title ?? 'Untitled',
      why: whenLabel(new Date(at), now, zoneOfCity(e.city)), at,
    })
  }
  saved.sort((a, b) => (a.kind === 'saved' && b.kind === 'saved' ? a.at - b.at : 0))

  return [...tasks, ...saved]
}

/** The hour (the row's own clock) from which "today" is an evening: 17:00, the line
 *  `today.ts`'s Tonight section draws at. Restated, not imported, because
 *  this file is dependency-free for the root suite. */
const TONIGHT_FROM_HOUR = 17

/**
 * The Shortlist's lead for a saved row that needs you: "Tonight 19:50",
 * "Tomorrow 11:00", "Tue 19:00" — or `null` outside the attention window.
 *
 * The window is not restated here: this asks `attentionItems` whether the row
 * is an item and reads `at` off the answer, so the Lists badge, the old
 * "needs you" sentence and this lead cannot disagree about who is in. Only the
 * wording is new — capitalised, because it now opens a line instead of
 * sitting mid-sentence, and "Tonight" for an evening start today (a morning
 * one stays "Today": the same split `today.ts` makes). A midnight start is
 * the placeholder for "a date, no time" and drops the clock.
 */
export function needYouPhrase(e: FeedLike, now: Date): string | null {
  const item = attentionItems([e], [{ entry_id: e.id, state: 'saved' }], [], now)[0]
  if (!item || item.kind !== 'saved') return null
  const at = new Date(item.at)
  const tz = zoneOfCity(e.city)
  const [dayWord, clock] = whenLabel(at, now, tz).split(' ')
  const lead = dayWord === 'today'
    ? (Number(clockIn(at, tz).slice(0, 2)) >= TONIGHT_FROM_HOUR ? 'Tonight' : 'Today')
    : dayWord[0].toUpperCase() + dayWord.slice(1)
  return clock === '00:00' ? lead : `${lead} ${clock}`
}

/** "1 needs you", "3 need you" — the lead of the Lists page's line. */
export const needsYou = (n: number) => `${n} ${n === 1 ? 'needs' : 'need'} you`
